import { Button, type ButtonProps } from '../Button';
import { ButtonType } from '@src/types';
import { classNames } from '@utils/classNames';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

type CancelSubmitButtonsProps = {
  onCancel: () => void;
  onSubmit: () => void | Promise<void>;
  passthrough?: {
    submitButton?: Partial<ButtonProps>;
    cancelButton?: Partial<ButtonProps>;
  };
};

export const CancelSubmitButtons = ({
  onCancel,
  onSubmit,
  passthrough,
}: CancelSubmitButtonsProps) => {
  const { t } = useTranslation('common');

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);
    try {
      await onSubmit();
    } finally {
      setIsSubmitting(false);
    }
  }, [onSubmit]);

  return (
    <div className="flex gap-4 self-end max-w-full">
      <Button
        label={t('button.cancel')}
        buttonType={ButtonType.SECONDARY}
        // /**
        //  * We need to prevent the onBlur event when clicking this button, otherwise we save the value even when pressing the cancel button
        //  * https://stackoverflow.com/a/57630197
        //  * That's why we prevent onMouseDown that the onBlur event is triggered and the onClick event can be called
        //  */
        onMouseDown={(e) => e.preventDefault()}
        onClick={onCancel}
        disabled={isSubmitting}
        {...passthrough?.cancelButton}
        labelClassName={classNames(
          'overflow-ellipsis block whitespace-nowrap overflow-hidden',
          passthrough?.cancelButton?.labelClassName,
        )}
        className={classNames(
          '!px-4 !py-1.5 flex-shrink min-w-0',
          passthrough?.cancelButton?.className,
        )}
      />
      <Button
        label={t('button.save')}
        buttonType={ButtonType.PRIMARY}
        // Prevents onBlur from being triggered if we want to press the save button
        onMouseDown={(e) => e.preventDefault()}
        onClick={handleSubmit}
        loading={isSubmitting}
        {...passthrough?.submitButton}
        className={classNames(
          '!px-4 !py-1.5 flex-shrink min-w-0',
          passthrough?.submitButton?.className,
        )}
        labelClassName={classNames(
          'overflow-ellipsis block whitespace-nowrap overflow-hidden',
          passthrough?.submitButton?.labelClassName,
        )}
      />
    </div>
  );
};
